/**
 * DuoScent Proben-Konfigurator (PDP + Modal)
 * Auswahl von exakt N Düften -> 1 Line Item "Probenset" mit Properties:
 *   Probe 1..N : <Duftname>   (sichtbar)
 *   _type      : probenset
 *   _set_id    : <uuid>
 *   _skus      : CODE,CODE,... (Reihenfolge = Probe 1..N)
 */
class ProbenKonfigurator extends HTMLElement {
  constructor() {
    super();
    this.required = parseInt(this.dataset.required, 10) || 6;
    this.addUrl = this.dataset.addUrl || '/cart/add.js';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.variantId = this.dataset.variantId || null;
    this.committed = [];
    this.draft = [];
    this.loading = false;
    this.atcDefaultHtml = this.atcBtn ? this.atcBtn.innerHTML : '';
  }

  connectedCallback() {
    this.modal = this.querySelector('[data-modal]');
    this.openBtns = this.querySelectorAll('[data-open]');
    this.openBtn = this.querySelector('[data-open]');
    this.openLabel = this.querySelector('[data-open-label]');
    this.searchInput = this.querySelector('[data-search]');
    this.items = Array.from(this.querySelectorAll('[data-probe-item]'));
    this.resultsEl = this.querySelector('[data-results]');
    this.countEl = this.querySelector('[data-count]');
    this.confirmBtn = this.querySelector('[data-confirm]');
    this.slots = Array.from(this.querySelectorAll('[data-slot]'));
    this.atcBtn = this.querySelector('[data-atc]');
    this.hintEl = this.querySelector('[data-hint]');
    this.errorEl = this.querySelector('[data-error]');
    this.summaryEl = this.querySelector('[data-summary]');
    this.summaryList = this.querySelector('[data-summary-list]');

    this.openBtns.forEach((b) => b.addEventListener('click', () => this.open()));
    this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    if (this.confirmBtn) this.confirmBtn.addEventListener('click', () => this.confirm());
    if (this.atcBtn) this.atcBtn.addEventListener('click', () => this.addToCart());
    if (this.searchInput) this.searchInput.addEventListener('input', (e) => this.filter(e.target.value));

    this.items.forEach((li) => {
      const btn = li.querySelector('.pk-item__btn');
      if (btn) btn.addEventListener('click', () => this.toggleItem(li));
    });
    this.slots.forEach((slot) => {
      const rm = slot.querySelector('[data-slot-remove]');
      if (rm) rm.addEventListener('click', () => this.removeSlot(parseInt(slot.dataset.index, 10) - 1));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && !this.modal.hidden) this.close();
    });

    const mainImg = this.querySelector('#pk-main-img');
    this.querySelectorAll('[data-thumb]').forEach((t) => {
      t.addEventListener('click', () => {
        if (mainImg && t.dataset.full) mainImg.src = t.dataset.full;
        this.querySelectorAll('[data-thumb]').forEach((x) => x.classList.remove('is-active'));
        t.classList.add('is-active');
      });
    });

    // Voraussichtliches Ankunftsdatum (heute + 2 Werktage, Sonntag übersprungen)
    const arrivalEl = this.querySelector('[data-arrival]');
    if (arrivalEl) arrivalEl.textContent = this.arrivalDate(2);

    this.filter('');
    this.syncPdp();
  }

  arrivalDate(workdays) {
    const d = new Date();
    let added = 0;
    while (added < workdays) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) added += 1; // Sonntag (0) zählt nicht
    }
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return `${days[d.getDay()]}, ${d.getDate()}. ${months[d.getMonth()]}`;
  }

  /* ---------- Modal ---------- */
  open() {
    this.resetAtc();
    this.draft = this.committed.slice();
    this.syncModal();
    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pk-lock');
    requestAnimationFrame(() => this.modal.classList.add('is-open'));
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.body.classList.remove('pk-lock');
    const done = () => {
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.removeEventListener('transitionend', done);
    };
    this.modal.addEventListener('transitionend', done);
    setTimeout(done, 350);
  }

  toggleItem(li) {
    const code = li.dataset.code || '';
    const name = li.dataset.name || '';
    const img = li.dataset.img || '';
    const key = this.keyOf(li);
    const idx = this.draft.findIndex((s) => this.keyStr(s) === key);
    if (idx > -1) {
      this.draft.splice(idx, 1);
    } else {
      if (this.draft.length >= this.required) return;
      this.draft.push({ code, name, img });
    }
    this.syncModal();
  }

  removeSlot(i) {
    if (i < 0 || i >= this.draft.length) return;
    this.draft.splice(i, 1);
    this.syncModal();
  }

  keyOf(li) { return (li.dataset.code || '') + '|' + (li.dataset.name || ''); }
  keyStr(s) { return (s.code || '') + '|' + (s.name || ''); }

  syncModal() {
    const atMax = this.draft.length >= this.required;
    this.items.forEach((li) => {
      const btn = li.querySelector('.pk-item__btn');
      const isSel = this.draft.some((s) => this.keyStr(s) === this.keyOf(li));
      btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      btn.classList.toggle('is-disabled', !isSel && atMax);
    });
    this.slots.forEach((slot, i) => {
      const nameEl = slot.querySelector('[data-slot-name]');
      const rm = slot.querySelector('[data-slot-remove]');
      const imgEl = slot.querySelector('[data-slot-img]');
      const entry = this.draft[i];
      if (entry) {
        slot.classList.add('is-filled');
        if (nameEl) nameEl.textContent = entry.name;
        if (rm) rm.hidden = false;
        if (imgEl) {
          if (entry.img) {
            imgEl.style.backgroundImage = `url("${entry.img}")`;
            slot.classList.add('has-image');
          } else {
            imgEl.style.backgroundImage = '';
            slot.classList.remove('has-image');
          }
        }
      } else {
        slot.classList.remove('is-filled');
        slot.classList.remove('has-image');
        if (nameEl) nameEl.textContent = '';
        if (rm) rm.hidden = true;
        if (imgEl) imgEl.style.backgroundImage = '';
      }
    });
    if (this.countEl) this.countEl.textContent = String(this.draft.length);
    if (this.confirmBtn) this.confirmBtn.disabled = this.draft.length !== this.required;
  }

  filter(q) {
    const query = (q || '').trim().toLowerCase();
    let visible = 0;
    this.items.forEach((li) => {
      const hay = li.dataset.searchText || '';
      const match = !query || hay.indexOf(query) > -1;
      li.classList.toggle('is-hidden', !match);
      if (match) visible += 1;
    });
    if (this.resultsEl) this.resultsEl.textContent = String(visible);
  }

  confirm() {
    if (this.draft.length !== this.required) return;
    this.committed = this.draft.slice();
    this.close();
    this.syncPdp();
  }

  /* ---------- PDP ---------- */
  syncPdp() {
    const complete = this.committed.length === this.required;
    if (this.openLabel) {
      this.openLabel.textContent = complete ? 'Probenauswahl bearbeiten' : `${this.required} Proben auswählen`;
    }
    if (this.openBtn) {
      this.openBtn.classList.toggle('pk-btn--primary', !complete);
      this.openBtn.classList.toggle('pk-btn--outline', complete);
    }
    if (this.summaryEl && this.summaryList) {
      this.summaryEl.hidden = !complete;
      if (complete) {
        this.summaryList.innerHTML = this.committed.map((s) => `<li>${this.esc(s.name)}</li>`).join('');
      }
    }
    if (this.hintEl) this.hintEl.hidden = complete;
    if (this.atcBtn) this.atcBtn.disabled = !complete || this.loading || !this.variantId;
  }

  /* ---------- Cart ---------- */
  async addToCart() {
    if (this.committed.length !== this.required || !this.variantId || this.loading) return;
    this.loading = true;
    this.setError('');
    this.atcBtn.disabled = true;

    const properties = { _type: 'probenset', _set_id: this.uuid() };
    this.committed.forEach((s, i) => { properties[`Probe ${i + 1}`] = s.name; });
    properties._skus = this.committed.map((s) => s.code).join(',');

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items: [{ id: Number(this.variantId), quantity: 1, properties }] };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const res = await fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status) {
        this.setError(data.description || data.message || 'Hinzufügen fehlgeschlagen.');
        return;
      }
      this.committed = [];
      this.draft = [];
      this.syncPdp();

     const bubbleHtml = data.sections && data.sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const fresh = doc.getElementById('cart-icon-bubble');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('proben:added', { detail: { properties } }));
      this.flashAdded();
    } catch (e) {
      this.setError('Netzwerkfehler. Bitte erneut versuchen.');
    } finally {
      this.loading = false;
      this.syncPdp();
    }
  }

  uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'set-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  setError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg || '';
    this.errorEl.hidden = !msg;
  }
  flashAdded() {
    if (!this.atcBtn) return;
    this.added = true;
    this.atcBtn.disabled = true;
    this.atcBtn.classList.remove('pk-btn--primary');
    this.atcBtn.classList.add('pk-btn--added');
    this.atcBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px"><path d="M20 6L9 17l-5-5"/></svg>Zum Warenkorb hinzugefügt';
  }

  resetAtc() {
    if (!this.atcBtn || !this.added) return;
    this.added = false;
    this.atcBtn.classList.remove('pk-btn--added');
    this.atcBtn.classList.add('pk-btn--primary');
    this.atcBtn.innerHTML = this.atcDefaultHtml;
  }
}

if (!customElements.get('proben-konfigurator')) {
  customElements.define('proben-konfigurator', ProbenKonfigurator);
}